import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { LogIn, Menu, ShieldCheck, ShoppingBag, UserRound, X } from "lucide-react";
import { doc, onSnapshot } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { db } from "../firebase";
import { isPublicPageListed, mergeSiteContent } from "../utils/helpers";
import AdminNavigation from "./AdminNavigation";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [site, setSite] = useState(() => mergeSiteContent());
  const location = useLocation();
  const { user } = useAuth();
  const { totals } = useCart();
  const isAdmin = location.pathname.startsWith("/admin");
  const solid = scrolled || isOpen || isAdmin;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    const off = onSnapshot(
      doc(db, "admin", "content"),
      (snap) => setSite(mergeSiteContent(snap.exists() ? snap.data() : {})),
      () => setSite(mergeSiteContent())
    );
    return () => off();
  }, []);

  const showPublicPage = (key) => isPublicPageListed(site, key);

  if (isAdmin) return <AdminNavigation />;

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-black/80 backdrop-blur-md shadow-md"
          : "bg-black/45 backdrop-blur-sm"
      }`}
    >
      <div className="flex items-center justify-between px-6 md:px-12 py-4">
        {/* Logo (toujours → accueil public) */}
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center">
            <img
              src="/logo.png"
              alt="Unlimited Session Logo"
              className="h-8 transition-all duration-300 cursor-pointer"
            />
          </Link>
        </div>

        {/* Menu Desktop */}
        <nav
          className="hidden md:flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm uppercase text-white transition-colors duration-300"
        >
          {isAdmin ? (
            <>
              <Link to="/admin" className="hover:text-red-500">Dashboard</Link>
              <Link to="/admin/accueil" className="hover:text-red-500">Accueil</Link>
              <Link to="/admin/latdior" className="hover:text-red-500">Latdior</Link>
              <Link to="/admin/artistes" className="hover:text-red-500">Artistes</Link>
              <Link to="/admin/news" className="hover:text-red-500">News</Link>
              <Link to="/admin/studio" className="hover:text-red-500">Studio</Link>
              <Link to="/admin/medias" className="hover:text-red-500">Médias</Link>
              <Link to="/admin/pages" className="hover:text-red-500">Pages</Link>
              <Link to="/admin/mentions-legales" className="hover:text-red-500">Mentions</Link>
              <Link to="/admin/typographie" className="hover:text-red-500">Typo</Link>
              <Link to="/admin/services" className="hover:text-red-500">Services</Link>
              <Link to="/admin/reservations" className="hover:text-red-500">Réservations</Link>
              <Link to="/admin/commandes" className="hover:text-red-500">Commandes</Link>
              <Link to="/admin/videos" className="hover:text-red-500">Vidéos</Link>
              <Link to="/admin/notifications" className="hover:text-red-500">Notifications</Link>
            </>
          ) : (
            <>
              {showPublicPage("home") && <Link to="/" className="hover:text-red-500">Accueil</Link>}
              {showPublicPage("latdior") && <Link to="/latdior" className="hover:text-red-500">Latdior</Link>}
              {showPublicPage("artistes") && <Link to="/artistes" className="hover:text-red-500">Artistes</Link>}
              {showPublicPage("news") && <Link to="/news" className="hover:text-red-500">News</Link>}
              {showPublicPage("studio") && <Link to="/studio" className="hover:text-red-500">Studio</Link>}
              {showPublicPage("services") && <Link to="/services" className="hover:text-red-500">Services</Link>}
              {showPublicPage("videos") && <Link to="/videos" className="hover:text-red-500">Vidéos</Link>}
            </>
          )}
        </nav>

        {/* Icônes compte / panier (uniquement public) */}
        <div className="hidden md:flex items-center gap-4 pr-2">
          {!isAdmin && (
            <>
              {showPublicPage("booking") && <Link
                to="/reservation-studio"
                className="rounded-full bg-white px-4 py-2 text-xs font-bold uppercase text-black transition hover:bg-red-600 hover:text-white"
              >
                Réserver
              </Link>}
              {showPublicPage("cart") && <Link
                to="/panier"
                className="relative text-gray-300 transition-colors duration-300 hover:text-white"
                aria-label="Panier"
              >
                <ShoppingBag className="h-5 w-5" />
                {totals.count > 0 && (
                  <span className="absolute -right-2 -top-2 grid h-4 min-w-4 place-items-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
                    {totals.count}
                  </span>
                )}
              </Link>}
              {showPublicPage(user ? "account" : "login") && <Link
                to={user ? "/compte" : "/connexion"}
                className="text-gray-300 transition-colors duration-300 hover:text-white"
                aria-label={user ? "Mon compte" : "Connexion"}
              >
                {user ? <UserRound className="h-5 w-5" /> : <LogIn className="h-5 w-5" />}
              </Link>}
              <Link
                to="/admin"
                className="text-gray-300 transition-colors duration-300 hover:text-white"
                aria-label="Connexion admin"
                title="Admin"
              >
                <ShieldCheck className="h-5 w-5" />
              </Link>
            </>
          )}
        </div>

        {/* Burger (mobile) */}
        <button
          className="md:hidden text-white transition-colors duration-300"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "Fermer le menu" : "Ouvrir le menu"}
          aria-expanded={isOpen}
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Menu Mobile */}
      {isOpen && createPortal(
      <>
      <button
        type="button"
        className="fixed inset-0 z-[70] bg-black/70 md:hidden"
        onClick={() => setIsOpen(false)}
        aria-label="Fermer le menu"
      />
      <div className="fixed right-0 top-0 z-[80] flex h-dvh w-[86vw] max-w-[360px] flex-col overflow-hidden border-l border-white/10 bg-black text-white shadow-2xl md:hidden">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <Link to="/" className="flex items-center" onClick={() => setIsOpen(false)}>
            <img
              src="/logo.png"
              alt="Unlimited Session Logo"
              className="h-8"
            />
          </Link>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-white transition hover:bg-white hover:text-black"
            aria-label="Fermer le menu"
          >
            <X size={22} />
          </button>
        </div>
        <nav className="flex flex-1 flex-col gap-3 overflow-y-auto px-5 py-5 text-base font-black uppercase tracking-wide">
          {isAdmin ? (
            <>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin" onClick={() => setIsOpen(false)}>Dashboard</Link>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin/accueil" onClick={() => setIsOpen(false)}>Accueil</Link>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin/latdior" onClick={() => setIsOpen(false)}>Latdior</Link>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin/artistes" onClick={() => setIsOpen(false)}>Artistes</Link>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin/news" onClick={() => setIsOpen(false)}>News</Link>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin/studio" onClick={() => setIsOpen(false)}>Studio</Link>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin/medias" onClick={() => setIsOpen(false)}>Médias globaux</Link>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin/pages" onClick={() => setIsOpen(false)}>Pages du site</Link>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin/mentions-legales" onClick={() => setIsOpen(false)}>Mentions légales</Link>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin/typographie" onClick={() => setIsOpen(false)}>Typographie</Link>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin/services" onClick={() => setIsOpen(false)}>Services</Link>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin/reservations" onClick={() => setIsOpen(false)}>Réservations</Link>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin/commandes" onClick={() => setIsOpen(false)}>Commandes</Link>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin/videos" onClick={() => setIsOpen(false)}>Vidéos</Link>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin/newsletter" onClick={() => setIsOpen(false)}>Newsletter</Link>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin/messages" onClick={() => setIsOpen(false)}>Messages</Link>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin/tarifs" onClick={() => setIsOpen(false)}>Tarifs</Link>
              <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/admin/parametres" onClick={() => setIsOpen(false)}>Paramètres</Link>
            </>
          ) : (
            <>
              {showPublicPage("home") && <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/" onClick={() => setIsOpen(false)}>Accueil</Link>}
              {showPublicPage("latdior") && <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/latdior" onClick={() => setIsOpen(false)}>Latdior</Link>}
              {showPublicPage("artistes") && <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/artistes" onClick={() => setIsOpen(false)}>Artistes</Link>}
              {showPublicPage("news") && <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/news" onClick={() => setIsOpen(false)}>News</Link>}
              {showPublicPage("studio") && <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/studio" onClick={() => setIsOpen(false)}>Studio</Link>}
              {showPublicPage("booking") && <Link className="rounded-lg border border-red-500/40 bg-red-600 px-4 py-3 text-white transition hover:bg-white hover:text-black" to="/reservation-studio" onClick={() => setIsOpen(false)}>Réserver</Link>}
              {showPublicPage("services") && <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/services" onClick={() => setIsOpen(false)}>Services</Link>}
              {showPublicPage("videos") && <Link className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500" to="/videos" onClick={() => setIsOpen(false)}>Vidéos</Link>}
              {showPublicPage("cart") && <Link to="/panier" onClick={() => setIsOpen(false)} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500">
                <ShoppingBag size={18} /> Panier {totals.count > 0 ? `(${totals.count})` : ""}
              </Link>}
              {showPublicPage(user ? "account" : "login") && <Link
                to={user ? "/compte" : "/connexion"}
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition hover:border-red-500 hover:text-red-500"
              >
                {user ? <UserRound size={18} /> : <LogIn size={18} />} {user ? "Mon compte" : "Connexion"}
              </Link>}
              <Link
                to="/admin"
                onClick={() => setIsOpen(false)}
                className="mt-2 flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-bold tracking-normal text-gray-400 transition hover:text-white"
              >
                <ShieldCheck size={18} /> Admin
              </Link>
            </>
          )}
        </nav>
      </div>
      </>
      , document.body)}
    </header>
  );
}
